package ru.chm.gradereport.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent

/** Value format: `yyyy-MM-dd` */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DatePickerField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
) {
    DatePickerFieldContent(
        value = value,
        onValueChange = onValueChange,
        label = label,
        modifier = modifier,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DatePickerFieldContent(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
) {
    val colors = GradeTheme.colors
    var showDate by remember { mutableStateOf(false) }
    val date = remember(value) { value.trim().take(10).ifBlank { "1970-01-01" } }
    val hover = rememberSoftHover()
    val (fieldBg, fieldBorder) = softFieldColors(
        state = hover,
        idleBg = colors.card,
        hoverBg = colors.fieldHover,
        pressBg = colors.fieldPress,
        idleBorder = colors.border,
        hoverBorder = GreenAccent.copy(alpha = 0.55f),
        pressBorder = GreenAccent.copy(alpha = 0.8f),
    )
    val shape = RoundedCornerShape(12.dp)

    Column(modifier = modifier) {
        Text(label, color = colors.textMuted, fontSize = 13.sp, modifier = Modifier.padding(bottom = 4.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .softHoverable(hover)
                .softFieldSurface(fieldBg, fieldBorder, shape)
                .clickable(
                    interactionSource = hover.interactionSource,
                    indication = null,
                    onClick = { showDate = true },
                )
                .padding(horizontal = 14.dp, vertical = 14.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(formatDisplayDate(value).ifBlank { "Выберите дату" }, color = colors.textPrimary, fontSize = 16.sp)
            Text("Изменить", color = GreenAccent, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
        }
    }

    if (showDate) {
        val dateState = rememberDatePickerState(
            initialSelectedDateMillis = dateToEpochMillisUtc(date),
        )
        DatePickerDialog(
            onDismissRequest = { showDate = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        val millis = dateState.selectedDateMillis
                        if (millis != null) {
                            onValueChange(epochMillisToDate(millis))
                            showDate = false
                        }
                    },
                ) { Text("Выбрать", color = GreenAccent) }
            },
            dismissButton = {
                TextButton(onClick = { showDate = false }) { Text("Отмена", color = colors.textMuted) }
            },
        ) {
            DatePicker(state = dateState)
        }
    }
}

private fun dateToEpochMillisUtc(date: String): Long {
    val y = date.substringBefore('-').toIntOrNull() ?: 1970
    val m = date.substring(5, 7).toIntOrNull() ?: 1
    val d = date.substring(8, 10).toIntOrNull() ?: 1
    val year = if (m <= 2) y - 1 else y
    val month = if (m <= 2) m + 9 else m - 3
    val era = (if (year >= 0) year else year - 399) / 400
    val yoe = year - era * 400
    val doy = (153 * month + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    val days = era * 146_097L + doe - 719_468
    return days * 86_400_000L
}

private fun epochMillisToDate(millis: Long): String {
    val days = millis / 86_400_000L
    var z = days + 719_468
    val era = (if (z >= 0) z else z - 146_096) / 146_097
    val doe = z - era * 146_097
    val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146_096) / 365
    var y = (yoe + era * 400).toInt()
    val doy = (doe - (365 * yoe + yoe / 4 - yoe / 100)).toInt()
    val mp = (5 * doy + 2) / 153
    val d = doy - (153 * mp + 2) / 5 + 1
    val m = mp + (if (mp < 10) 3 else -9)
    y += if (m <= 2) 1 else 0
    return "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
}
